import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ColumnsType } from 'antd/lib/table';
import FormDrawer from '~/components/FormDrawer';
import { AgentApplication } from '~/types/AgentApplication';
import { AssigneeSelect, Button, TextField } from '~/components';
import { openNewApplicationTab } from '~/utils/goToApplication';
import TerminationDrawer from '~/pages/agent/components/agent/TerminationDrawer';
import {
  useAssignApplication,
  useGetApplicationsList,
  useTerminateApplication,
} from '~/controllers/agent';
import GroupLayout from '~/layouts/GroupLayout';
import { AgentLead } from '~/types/AgentLead';
import {
  businessLineCell,
  createdCell,
  regionCell,
  riskLevelCell,
  statusCell,
  submittedCell,
  updatedCell,
  applicationIdCell,
  crUnifiedNumberCell,
} from '~/pages/agent/components/agent/tableElements/cells';
import { useHandbook } from '~/controllers/handbook';
import { queryClient } from '~/index';
import { getDecodedJwt, isTeamLead } from '~/utils/getDecodedJwt';
import { DeleteIcon } from '~/img';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import FloatLabel from '~/pages/ksa/dataValidation/components/FloatLabel';

interface Props {
  open: boolean;
  onClose: () => void;
  record?: AgentApplication;
}

const ApplicationShow: FC<Props> = (props) => {
  const { record: recordProp, open, onClose } = props;
  const [t] = useTranslation('agent');
  const [terminatedOpen, setTerminatedOpen] = useState(false);
  const { data: handbook } = useHandbook();
  const terminateApplication = useTerminateApplication();
  const { data } = useGetApplicationsList();
  const assignApplication = useAssignApplication();
  //

  const record =
    data?.data?.find(
      (item: AgentApplication) => item?.applicationId === recordProp?.applicationId
    ) || recordProp;
  if (!record) return null;

  const { emirate, statusKey, businessLine, riskLevel, created, updated, assignee, applicationId, crUnifiedNumber } = record;
  const isAssignedToCurrentUser = getDecodedJwt()?.email !== record.assignee?.email;
  const isTerminated = record?.statusKey === 'terminated';

  const onSelect = (agentId: string) => {
    assignApplication
      .mutateAsync({ applicationId: record.applicationId, agentId })
      .then(async () => {
        await queryClient.refetchQueries('applications');
      })
      .catch(() => {});
  };

  return (
    <>
      <FormDrawer
        id="application"
        visible={open}
        onClose={onClose}
        title={record?.companyName}
        closable
        formName={formName}
        withCustomButtons
        withContinueButton={false}
        customButtonsProps={[
          {
            id: 'goToApplicationButton',
            title: t('Go to Application'),
            type: 'primary',
            onClick: () => openNewApplicationTab(record.applicationId),
            disabled: isAssignedToCurrentUser,
          },
        ]}
      >
        <Container>
          <GroupLayout gutter={[16, 16]} xs={12} sm={12} className="ApplicationData">
            <TextField id="ApplicationShow_applicationId" label={t('Application ID')}>
              <>{render(applicationIdCell(), applicationId, record)}</>
            </TextField>
            <TextField id="ApplicationShow_crUnifiedNumber" label={t('CR Number')}>
              <>{render(crUnifiedNumberCell(), crUnifiedNumber, record)}</>
            </TextField>
            <TextField id="ApplicationShow_region" label={t('Region')}>
              <>{render(regionCell(handbook), emirate, record)}</>
            </TextField>
            <TextField id="ApplicationShow_status" label={t('Status')}>
              <>{render(statusCell(), statusKey, record)}</>
            </TextField>
            <TextField id="ApplicationShow_businessLine" label={t('Business line')}>
              <>{render(businessLineCell(handbook), businessLine, record)}</>
            </TextField>
            <TextField id="ApplicationShow_riskLevel" label={t('Risk level')}>
              <>{render(riskLevelCell('APPLICATION'), riskLevel, record)}</>
            </TextField>
            <TextField id="ApplicationShow_created" label={t('Created')}>
              <>{render(createdCell(), created, record)}</>
            </TextField>
            <TextField id="ApplicationShow_submitted" label={t('Submitted')}>
              <>{render(submittedCell(), created, record)}</>
            </TextField>
            <TextField id="ApplicationShow_updated" label={t('Updated')}>
              <>{render(updatedCell(), updated, record)}</>
            </TextField>
          </GroupLayout>
          <InputsGroupLayout>
            <FloatLabel label={t('Assignee')} value={assignee?.id}>
              <AssigneeSelect onSelect={onSelect} style={{ width: '100%' }} hasSimpleView />
            </FloatLabel>
          </InputsGroupLayout>
        </Container>
        <ApplicationTerminateButton
          id="terminateApplicationButton"
          type="default"
          block
          danger
          icon={<DeleteIcon />}
          disabled={isTerminated}
          onClick={() => setTerminatedOpen(true)}
        >
          {t('Terminate Application')}
        </ApplicationTerminateButton>
      </FormDrawer>
      <TerminationDrawer
        visible={terminatedOpen}
        companyName={record?.companyName}
        onTerminate={async (terminationReason) => {
          if (record?.applicationId) {
            await terminateApplication.mutateAsync({
              id: record.applicationId,
              terminationReason,
            });
            await queryClient.refetchQueries('applications');
          }
        }}
        onClose={() => {
          setTerminatedOpen(false);
          onClose();
        }}
      />
    </>
  );
};

const formName = 'applicationAssignee';

const render = (f: ColumnsType<any>[0], value: any, record: AgentApplication | AgentLead) =>
  f.render?.(value, record, 0);

const Container = styled.div`
  flex: 1;

  .ApplicationData {
    margin-bottom: 24px;
  }
`;

const ApplicationTerminateButton = styled(Button)`
  margin-bottom: 24px;
`;

export default ApplicationShow;
